import React, { useState } from 'react';
import { Box, Card, CardContent, Chip, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import type { Milestone } from '../../services/household-retirement-service';
import { monthYear } from './format';

const KIND_COLOR: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'> = {
  retirement: 'primary', household_retired: 'primary', cpp: 'info', oas: 'info', debt_paid: 'success',
  dca_deployed: 'success', childcare: 'secondary', education: 'secondary', leave: 'warning', depleted: 'error',
};

const KIND_LABEL: Record<string, string> = {
  retirement: 'Retirement', household_retired: 'Retired', cpp: 'CPP', oas: 'OAS', debt_paid: 'Debt',
  dca_deployed: 'DCA', childcare: 'Daycare', education: 'School', leave: 'Leave', depleted: 'Risk',
};

/** The plan's dated events, in order: leave, daycare, debts, the DCA pool,
 * university, retirements, CPP/OAS. Past events are hidden by default. */
export const MilestonesTimeline: React.FC<{ milestones: Milestone[] }> = ({ milestones }) => {
  const [past, setPast] = useState(false);
  const shown = milestones.filter((m) => past || !m.past);
  return (
    <Card>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="overline" color="text.secondary">Milestones</Typography>
          <FormControlLabel control={<Switch size="small" checked={past} onChange={(e) => setPast(e.target.checked)} />} label="Show past" />
        </Stack>
        <Box component="ol" aria-label="Milestones timeline" sx={{ listStyle: 'none', m: 0, p: 0, position: 'relative',
          '&::before': { content: '""', position: 'absolute', left: 86, top: 4, bottom: 4, width: 2, bgcolor: 'divider' } }}>
          {shown.map((m, i) => (
            <Box component="li" key={`${m.date}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5, opacity: m.past ? 0.55 : 1 }}>
              <Typography variant="body2" className="fw-num" sx={{ width: 72, flexShrink: 0, color: 'text.secondary' }}>
                {monthYear(m.date)}
              </Typography>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: `${KIND_COLOR[m.kind] ?? 'grey'}.main`,
                border: 2, borderColor: 'background.paper', zIndex: 1, flexShrink: 0 }} />
              <Chip size="small" label={KIND_LABEL[m.kind] ?? m.kind} color={KIND_COLOR[m.kind] ?? 'default'} variant="outlined"
                sx={{ height: 20, minWidth: 64 }} />
              <Typography variant="body2">{m.label}</Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};
